import { useEffect, useState } from "react";
import { createRootRoute, Outlet, useRouterState } from "@tanstack/react-router";

/*
  Turns "/integrations/personify/identities/42" into "Integrations / Personify /
  Identities", dropping record ids so the label stays readable.
*/
const describeLocation = (pathname: string) => {
  const segments = pathname
    .split("/")
    .filter(Boolean)
    .filter((segment) => {
      return !/^\d+$/.test(segment);
    })
    .map((segment) => {
      const words = segment.replace(/[_-]+/g, " ");

      return words.charAt(0).toUpperCase() + words.slice(1);
    });

  return segments.length ? segments.join(" / ") : "Home";
};

/*
  A client-side navigation replaces the page without telling assistive tech
  anything changed. This announces the new location and keeps the document title
  in step, which the static Blade title never did.
*/
const RouteAnnouncer = () => {
  const pathname = useRouterState({
    select: (state) => {
      return state.location.pathname;
    },
  });

  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    const label = describeLocation(pathname);

    document.title = `${label} · EO Admin`;
    setAnnouncement(`${label} page loaded`);
  }, [pathname]);

  return (
    <div aria-atomic="true" aria-live="polite" className="sr-only" role="status">
      {announcement}
    </div>
  );
};

const RootComponent = () => {
  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      {/*
        First thing in the tab order: lets keyboard and screen-reader users jump
        past the sidebar instead of walking every nav item on every page.
      */}
      <a
        className="skip-link rounded-lg bg-accent px-4 py-2 text-sm font-medium text-content-on-accent shadow-overlay focus-visible:outline-none"
        href="#main-content"
      >
        Skip to main content
      </a>

      <RouteAnnouncer />

      <Outlet />
    </div>
  );
};

export const Route = createRootRoute({ component: RootComponent });
